import React, { useEffect, useState } from 'react';
import {
    ActionIcon,
    Alert as MantineAlert,
    Badge,
    Flex,
    Modal,
    Paper,
    ScrollArea,
    Stack,
    Table,
    Text,
    TextInput,
    ThemeIcon,
    Timeline,
    Title,
    Tooltip,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IconInfoCircle, IconMail, IconSearch } from '@tabler/icons-react';
import { TableContextProvider, Th, useTable } from '../../../global/hooks/useTable.tsx';
import { usePatientAlerts, usePatientAlertNotifications } from '../../../global/entities/Alert/queries';
import { useMarkAllPatientAlertsVisited } from '../../../global/entities/Alert/mutations';
import { Loading } from '../../../global/component/organism/Loading/Loading.tsx';
import { IClinicalAlert } from '../../../global/entities/Alert/Alert.ts';
import Utils from '../../../global/helpers/utils.ts';
import tableTools from '../../../global/helpers/tableTools.ts';
import { containsStringIgnoreCase } from '../../HcpDashboard/Table/PatientTable.tsx';

const NotificationModalContent: React.FC<{ alertId: number }> = ({ alertId }) => {
    const { t } = useTranslation();
    const { data: notifications, isLoading } = usePatientAlertNotifications(alertId);

    if (isLoading)
        return <Text size="sm" c="dimmed" ta="center" py="xs">{t('global.loading')}</Text>;

    if (!notifications || notifications.length === 0)
        return <Text size="sm" c="dimmed" ta="center" py="xs">{t('alert.notify.noNotifications')}</Text>;

    return (
        <Timeline
            active={notifications.length - 1}
            bulletSize={24}
            lineWidth={2}
            color="cyan"
        >
            {notifications.map((notif) => (
                <Timeline.Item
                    key={notif.id}
                    bullet={
                        <ThemeIcon size={24} radius="xl" variant="light" color="cyan">
                            <IconMail size={12} />
                        </ThemeIcon>
                    }
                    title={
                        <Text size="xs" c="dimmed">
                            {Utils.formatLocalDateToYYYYMMDD(notif.sentDate)}{' '}
                            {Utils.formatLocalDateToTime(notif.sentDate)}
                            {notif.sentByUserName && ` — ${notif.sentByUserName}`}
                        </Text>
                    }
                >
                    <Paper p="sm" bg="gray.0" radius="sm" mt={4}>
                        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{notif.message}</Text>
                    </Paper>
                </Timeline.Item>
            ))}
        </Timeline>
    );
};

/** Map internal status to patient-facing display */
const getPatientStatus = (status: string): string => {
    if (status === 'Escalated') return 'Acknowledged';
    return status;
};

const getPatientStatusKey = (status: string): string => {
    if (status === 'Escalated') return 'acknowledged';
    return status.toLowerCase();
};

const patientStatusColor = (status: string): string => {
    const mapped = getPatientStatus(status);
    switch (mapped) {
        case 'Open': return 'red';
        case 'Acknowledged': return 'teal';
        case 'Resolved': return 'green';
        default: return 'gray';
    }
};

const PatientAlertTable: React.FC = () => {
    const { t } = useTranslation();
    const { sortedData, setSortedData, sortBy, reverseSortDirection, setSorting } = useTable();
    const [search, setSearch] = useState('');
    const [selectedAlertId, setSelectedAlertId] = useState<number | null>(null);
    const [unreadNotificationIds, setUnreadNotificationIds] = useState<number[]>([]);
    const [autoOpenHandled, setAutoOpenHandled] = useState(false);
    const { data: alerts, isLoading } = usePatientAlerts();
    const markAllVisited = useMarkAllPatientAlertsVisited();

    // Auto-open notification modal for alerts re-activated by a new HCP message
    useEffect(() => {
        if (!alerts || autoOpenHandled) return;

        const unreadWithNotifications = alerts
            .filter((a) => !a.isVisited && a.patientNotified)
            .map((a) => a.id);

        if (unreadWithNotifications.length > 0) {
            setUnreadNotificationIds(unreadWithNotifications);
            setSelectedAlertId(unreadWithNotifications[0]);
        } else if (alerts.some((a) => !a.isVisited) && markAllVisited.isIdle) 
            markAllVisited.mutate();
        
        setAutoOpenHandled(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alerts]);

    useEffect(() => {
        if (alerts)
            setSortedData(tableTools.sortData([...alerts], { sortBy: 'severity', reversed: false, search: '' }));

    }, [alerts, setSortedData]);

    const getPatientDescription = (alert: IClinicalAlert): string => {
        const key = `alert.patient.description.${alert.sourceType}`;
        const translated = t(key);
        // If key not found (returns key itself), use default
        if (translated === key) return t('alert.patient.description.default');
        return translated;
    };

    if (isLoading)
        return <Loading visible={true} loadingMessage={t('global.loading')} />;

    return (
        <Stack gap={'lg'} pt={'xl'} h={'100%'}>
            <Title c="black" order={3}>
                {t('alert.title')}
            </Title>

            {/* 12.4 Reassuring note */}
            <MantineAlert
                icon={<IconInfoCircle size={18} />}
                color="blue"
                variant="light"
                radius="md"
            >
                {t('alert.patient.reassuringNote')}
            </MantineAlert>

            <Paper w="100%">
                <Stack>
                    <Flex align={'center'} gap={'sm'}>
                        <TextInput
                            placeholder={t('global.search')}
                            rightSection={<IconSearch />}
                            value={search}
                            color={'var(--mantine-color-default-color)'}
                            style={{ flex: 1 }}
                            radius={'md'}
                            onChange={(el) => setSearch(el.currentTarget.value)}
                        />
                    </Flex>
                    <ScrollArea w="100%">
                        <Table verticalSpacing="xs" style={{ tableLayout: 'auto' }}>
                            <Table.Thead>
                                <Table.Tr>
                                    <Th
                                        sorted={sortBy === 'triggeredDate'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('triggeredDate', sortedData)}
                                    >
                                        {t('alert.table.date')}
                                    </Th>
                                    <Th
                                        sorted={sortBy === 'titleEn'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('titleEn', sortedData)}
                                    >
                                        {t('alert.table.description')}
                                    </Th>
                                    <Th
                                        sorted={sortBy === 'status'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('status', sortedData)}
                                    >
                                        {t('alert.table.status')}
                                    </Th>
                                    <Table.Th style={{ width: 50 }} />
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {sortedData?.length === 0 && (
                                    <Table.Tr>
                                        <Table.Td colSpan={4}>
                                            <Text ta="center" c="dimmed" py="md">
                                                {t('alert.noAlerts')}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                )}
                                {sortedData?.map((alert: IClinicalAlert, index: number) => {
                                    const displayText = getPatientDescription(alert);
                                    const statusKey = getPatientStatusKey(alert.status);
                                    const isSearch =
                                        containsStringIgnoreCase(
                                            [
                                                displayText,
                                                t(`alert.status.${statusKey}`),
                                            ],
                                            search,
                                        ) || !search;
                                    return (
                                        <Table.Tr
                                            key={alert.id}
                                            hidden={!isSearch}
                                            style={{
                                                background:
                                                    index % 2 === 1
                                                        ? undefined
                                                        : 'var(--Background-Gray-light, #E9ECEF)',
                                            }}
                                        >
                                            <Table.Td>
                                                <Text fw={500} size={'sm'}>
                                                    {Utils.formatLocalDateToYYYYMMDD(alert.triggeredDate)}{' '}
                                                    {Utils.formatLocalDateToTime(alert.triggeredDate)}
                                                </Text>
                                            </Table.Td>
                                            <Table.Td>
                                                <Tooltip
                                                    label={t(`alert.patient.statusDescription.${statusKey}`)}
                                                    multiline
                                                    withArrow
                                                    position="top"
                                                    styles={{
                                                        tooltip: {
                                                            fontSize: '13px',
                                                            lineHeight: '1.4',
                                                            maxWidth: '320px',
                                                            whiteSpace: 'pre-wrap',
                                                        },
                                                    }}
                                                >
                                                    <Text fw={500} size={'sm'}>
                                                        {displayText}
                                                    </Text>
                                                </Tooltip>
                                            </Table.Td>
                                            <Table.Td>
                                                <Badge
                                                    color={patientStatusColor(alert.status)}
                                                    variant="light"
                                                    size="sm"
                                                >
                                                    {t(`alert.status.${statusKey}`)}
                                                </Badge>
                                            </Table.Td>
                                            <Table.Td>
                                                {alert.patientNotified && (
                                                    <Tooltip label={t('alert.notify.viewMessages')}>
                                                        <ActionIcon
                                                            variant="light"
                                                            color="blue"
                                                            size="sm"
                                                            onClick={() => setSelectedAlertId(alert.id)}
                                                        >
                                                            <IconMail size={14} />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                )}
                                            </Table.Td>
                                        </Table.Tr>
                                    );
                                })}
                            </Table.Tbody>
                        </Table>
                    </ScrollArea>
                </Stack>
            </Paper>

            <Modal
                opened={selectedAlertId !== null}
                onClose={() => {
                    // Move to next unread notification, or mark all visited when done
                    const remaining = unreadNotificationIds.filter((id) => id !== selectedAlertId);
                    setUnreadNotificationIds(remaining);
                    if (remaining.length > 0) 
                        setSelectedAlertId(remaining[0]);
                     else {
                        setSelectedAlertId(null);
                        if (alerts && alerts.some((a) => !a.isVisited) && markAllVisited.isIdle)
                            markAllVisited.mutate();
                    }
                }}
                title={t('alert.notify.history')}
                size="md"
            >
                {selectedAlertId && <NotificationModalContent alertId={selectedAlertId} />}
            </Modal>
        </Stack>
    );
};

const PatientAlerts: React.FC = () => {
    return (
        <TableContextProvider>
            <PatientAlertTable />
        </TableContextProvider>
    );
};

export default PatientAlerts;
